import { ActionContext } from 'vuex';
import { Survey, SurveyType } from '@/models/Survey';
import { Store } from '@/models/Store';

const state: Survey = {
  type: SurveyType.call,
  isAgreed: false,
  recommendationScale: 999,
  recommendationComment: '',
  faqViewStatus: '',
  continueSupportService: 0,
  supportServiceComment: '',
  supportServiceRating: 0,
  faqHelpfulnessScale: 0,
  faqUnhelpfulnessReasons: [],
  faqUnhelpfulnessOtherReason: '',
  supportHelpfulnessScale: 0,
  supportUnhelpfulnessReason: '',
  supportUnhelpfulnessOtherReason: '',
  enquiryRating: 0,
  operatorRating: 0,
  hasBeenResolved: true,
  age: '',
  literacy: '',
  gender: '',
  deviceSupportNeeds: '',
  deviceSupportFeedbackRequest: ''
};

const getters = {
  type (moduleState: Survey) {
    return moduleState.type;
  },
  isCallSurvey (moduleState: Survey) {
    return moduleState.type === SurveyType.call;
  },
  permission (moduleState: Survey) {
    return moduleState.isAgreed;
  },
  recommendationScale (moduleState: Survey) {
    return moduleState.recommendationScale;
  },
  recommendationComment (moduleState: Survey) {
    return moduleState.recommendationComment;
  },
  continueSupportService (moduleState: Survey) {
    return moduleState.continueSupportService;
  },
  supportServiceComment (moduleState: Survey) {
    return moduleState.supportServiceComment;
  },
  supportServiceRating (moduleState: Survey) {
    return moduleState.supportServiceRating;
  },
  faqViewStatus (moduleState: Survey) {
    return moduleState.faqViewStatus;
  },
  faqHelpfulnessScale (moduleState: Survey) {
    return moduleState.faqHelpfulnessScale;
  },
  faqUnhelpfulnessReasons (moduleState: Survey) {
    return moduleState.faqUnhelpfulnessReasons;
  },
  faqUnhelpfulnessOtherReason (moduleState: Survey) {
    return moduleState.faqUnhelpfulnessOtherReason;
  },

  supportHelpfulnessScale (moduleState: Survey) {
    return moduleState.supportHelpfulnessScale;
  },
  supportUnhelpfulnessReason (moduleState: Survey) {
    return moduleState.supportUnhelpfulnessReason;
  },
  supportUnhelpfulnessOtherReason (moduleState: Survey) {
    return moduleState.supportUnhelpfulnessOtherReason;
  },
  enquiryRating (moduleState: Survey) {
    return moduleState.enquiryRating;
  },
  operatorRating (moduleState: Survey) {
    return moduleState.operatorRating;
  },
  hasBeenResolved (moduleState: Survey) {
    return moduleState.hasBeenResolved;
  },
  getAge (moduleState: Survey) {
    return moduleState.age;
  },
  literacy (moduleState: Survey) {
    return moduleState.literacy;
  },
  getGender (moduleState: Survey) {
    return moduleState.gender;
  },
  deviceSupportFeedbackRequest (moduleState: Survey) {
    return moduleState.deviceSupportFeedbackRequest;
  },
  deviceSupportNeeds (moduleState: Survey) {
    return moduleState.deviceSupportNeeds;
  }
};

const mutations = {
  setType (moduleState: Survey, type: SurveyType) {
    moduleState.type = type;
  },
  setPermission (moduleState: Survey, permission: boolean) {
    moduleState.isAgreed = permission;
  },
  setRecommendationScale (moduleState: Survey, content: number) {
    moduleState.recommendationScale = content;
  },
  setRecommendationComment (moduleState: Survey, content: string) {
    moduleState.recommendationComment = content;
  },
  setContinueSupportService (moduleState: Survey, rating: number) {
    moduleState.continueSupportService = rating;
  },
  setSupportServiceComment (moduleState: Survey, content: string) {
    moduleState.supportServiceComment = content;
  },
  setSupportServiceRating (moduleState: Survey, rating: number) {
    moduleState.supportServiceRating = rating;
  },
  setFaqViewStatus (moduleState: Survey, isViewed: string) {
    moduleState.faqViewStatus = isViewed;
  },
  setFaqHelpfulnessScale (moduleState: Survey, point: number) {
    moduleState.faqHelpfulnessScale = point;
  },
  setFaqUnhelpfulnessReasons (moduleState: Survey, review: string[]) {
    moduleState.faqUnhelpfulnessReasons = review;
  },
  setFaqUnhelpfulnessOtherReason (moduleState: Survey, details: string) {
    moduleState.faqUnhelpfulnessOtherReason = details;
  },

  setSupportHelpfulnessScale (moduleState: Survey, point: number) {
    moduleState.supportHelpfulnessScale = point;
  },
  setSupportUnhelpfulnessReason (moduleState: Survey, review: string) {
    moduleState.supportUnhelpfulnessReason = review;
  },
  setSupportUnhelpfulnessOtherReason (moduleState: Survey, details: string) {
    moduleState.supportUnhelpfulnessOtherReason = details;
  },
  setEnquiryRating (moduleState: Survey, rating: number) {
    moduleState.enquiryRating = rating;
  },
  setOperatorRating (moduleState: Survey, rating: number) {
    moduleState.operatorRating = rating;
  },
  setResolvedStatus (moduleState: Survey, isSolved: boolean) {
    moduleState.hasBeenResolved = isSolved;
  },
  setAge (moduleState: Survey, age: string) {
    moduleState.age = age;
  },
  setLiteracy (moduleState: Survey, literacy: string) {
    moduleState.literacy = literacy;
  },
  setGender (moduleState: Survey, gender: string) {
    moduleState.gender = gender;
  },
  setDeviceSupportFeedbackRequest (moduleState: Survey, feedback: string) {
    moduleState.deviceSupportFeedbackRequest = feedback;
  },
  setDeviceSupportNeeds (moduleState: Survey, needs: string) {
    moduleState.deviceSupportNeeds = needs;
  }
};

const actions = {
  changeType (action: ActionContext<Survey, Store>, type: string) {
    action.commit('setType', type);
  },
  changePermission (action: ActionContext<Survey, Store>, permission: boolean) {
    action.commit('setPermission', permission);
  },

  changeRecommendationScale (action: ActionContext<Survey, Store>, servicePoint: number) {
    action.commit('setRecommendationScale', servicePoint);
  },
  changeRecommendationComment (action: ActionContext<Survey, Store>, servicePointDetails: string) {
    action.commit('setRecommendationComment', servicePointDetails);
  },
  changeContinueSupportService (action: ActionContext<Survey, Store>, continueSupportService: number) {
    action.commit('setContinueSupportService', continueSupportService);
  },
  changeSupportServiceComment (action: ActionContext<Survey, Store>, supportServicePointDetails: string) {
    action.commit('setSupportServiceComment', supportServicePointDetails);
  },
  changeSupportServiceRating (action: ActionContext<Survey, Store>, supportServiceRating: number) {
    action.commit('setSupportServiceRating', supportServiceRating);
  },
  changeFaqViewStatus (action: ActionContext<Survey, Store>, pageStatus: string) {
    action.commit('setFaqViewStatus', pageStatus);
  },
  changeFaqHelpfulnessScale (action: ActionContext<Survey, Store>, troublePagePoint: number) {
    action.commit('setFaqHelpfulnessScale', troublePagePoint);
  },
  changeFaqUnhelpfulnessReasons (action: ActionContext<Survey, Store>, troublePageReview: string[]) {
    action.commit('setFaqUnhelpfulnessReasons', troublePageReview);
  },
  changeFaqUnhelpfulnessOtherReason (action: ActionContext<Survey, Store>, supportPagePointDetails: string) {
    action.commit('setFaqUnhelpfulnessOtherReason', supportPagePointDetails);
  },

  changeSupportHelpfulnessScale (action: ActionContext<Survey, Store>, supportCenterPoint: number) {
    action.commit('setSupportHelpfulnessScale', supportCenterPoint);
  },
  changeSupportUnhelpfulnessReason (action: ActionContext<Survey, Store>, supportCenterReview: string) {
    action.commit('setSupportUnhelpfulnessReason', supportCenterReview);
  },
  changeSupportUnhelpfulnessOtherReason (action: ActionContext<Survey, Store>, supportCenterDetails: string) {
    action.commit('setSupportUnhelpfulnessOtherReason', supportCenterDetails);
  },
  changeEnquiryRating (action: ActionContext<Survey, Store>, enquiryRating: number) {
    action.commit('setEnquiryRating', enquiryRating);
  },
  changeOperatorRating (action: ActionContext<Survey, Store>, operatorRating: number) {
    action.commit('setOperatorRating', operatorRating);
  },
  changeResolvedStatus (action: ActionContext<Survey, Store>, problemStatus: boolean) {
    action.commit('setResolvedStatus', problemStatus);
  },
  changeAge (action: ActionContext<Survey, Store>, age: string) {
    action.commit('setAge', age);
  },
  changeLiteracy (action: ActionContext<Survey, Store>, literacy: string) {
    action.commit('setLiteracy', literacy);
  },
  changeGender (action: ActionContext<Survey, Store>, gender: string) {
    action.commit('setGender', gender);
  },
  changeDeviceSupportFeedbackRequest (action: ActionContext<Survey, Store>, feedback: string) {
    action.commit('setDeviceSupportFeedbackRequest', feedback);
  },
  changeDeviceSupportNeeds (action: ActionContext<Survey, Store>, needs: string) {
    action.commit('setDeviceSupportNeeds', needs);
  }
};

export const survey = {
  state,
  getters,
  mutations,
  actions
};
